import React, { useEffect, useState } from 'react';
import { Box, Typography, Container, Snackbar, Alert } from '@mui/material';
import { Dropzone, FileItem, ExtFile } from '@dropzone-ui/react';
import { AddButton } from '../button/button';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { useDispatch, useSelector } from 'react-redux';
import { clearFile, uploadFile, readSheets } from '../../redux/reducers/sheetAllocationType3';
import { AppDispatch, RootState } from '../../redux/store/appStore';
import { getDownloadTemplate, getDownloadTemplateStatusReset } from '../../redux/slices/vendorList';
import { useParams } from 'react-router-dom';
import { STATUS } from '../../utils/status';
import SnackbarNotification from '../snackbar/snackbarNotification';

interface UploadFileVendorAllocationProps {
  setSheets?: any;
}

const UploadFileVendorAllocation: React.FC<UploadFileVendorAllocationProps> = ({ setSheets }) => {
  const [files, setFiles] = useState<ExtFile[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');
  const dispatch = useDispatch<AppDispatch>();
  const { id } = useParams();
  const { file, sheets } = useSelector((state: RootState) => state.fileAllocation);
  const path = window.location.href.includes('vendorbulkallocation')

  const downloadTemplateStatus = useSelector((state: RootState) => state.vendorList.getDownloadTemplateStatus);
  const downloadTemplateLink = useSelector((state: RootState) => state.vendorList.template);

  useEffect(() => {
    if (
      downloadTemplateStatus === "SUCESS" &&
      downloadTemplateLink?.data?.download_url
    ) {
      const link = document.createElement("a");
      link.href = downloadTemplateLink.data.download_url;
      link.setAttribute("download", "template.xlsx");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      handleSnackbar('Template downloaded successfully', 'success');
      dispatch(getDownloadTemplateStatusReset(STATUS.NOT_STARTED));
    }
  }, [downloadTemplateStatus, downloadTemplateLink]);

  useEffect(() => {
    if (file) {
      setFiles([{ file }]);
    }
  }, [file]);

  useEffect(() => {
    if (sheets) {
      setSheets(sheets);
    }
  }, [sheets, setSheets]);

  const handleDrop = (newFiles: ExtFile[]) => {
    setFiles(newFiles);
    if (newFiles.length > 0) {
      const file = newFiles[0].file;
      if (file) {
        dispatch(uploadFile(file));
        dispatch(readSheets(file));
      }
    }
  };

  const handleDownloadTemplateClick = () => {
    const payload = {
      template_name: path ? 'Vendor_Bulk_Allocation_Template' : 'Vendor_Bulk_UnAllocation_Template',
      project_id: id,
      file_type: 'xlsx'
    };
    dispatch(getDownloadTemplate(payload));
  }

  const handleDelete = () => {
    setFiles([]);
    dispatch(clearFile());
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false); // reset snackbar
  };

  const handleSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  return (
    <Container maxWidth="md">
      <Box sx={{
        background: 'linear-gradient(135deg, #4a90e2, #add8e6)',
        borderRadius: '10px',
        padding: '20px',
        marginTop: '20px',
        boxShadow: 3,
      }}>
        <Typography variant="h6" align="center" sx={{ color: '#fff', marginBottom: '20px', fontWeight: '600' }}>
          Upload Your File
        </Typography>
        <Box mt={2} display="flex" flexDirection="column" alignItems="center" position="relative">
          <Dropzone
            onChange={handleDrop}
            accept={'.csv,.xls,.xlsx'}
            maxFiles={1}
            header={false}
            footer={false}
            style={{
              border: '2px dashed #4a90e2',
              borderRadius: '10px',
              padding: '20px',
              backgroundColor: '#ffffff',
              width: '100%',
              textAlign: 'center',
              cursor: 'pointer'
            }}
          >
            {files.length === 0 ? (
              <>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body2" sx={{ textTransform: 'none', fontSize: '1rem', fontWeight: 'bold', color: '#333' }}>
                    Upload a file here
                  </Typography>
                  <Typography variant="body2" sx={{ textTransform: 'none', fontSize: '14px', color: '#333', marginTop: '10px' }}>
                    You can upload excel(.xlsx) files and csv files
                  </Typography>
                  <AddButton>Choose a file</AddButton>
                </div>
              </>
            ) : (
              files.map((file, index) => (
                <FileItem
                  key={index}
                  file={file.file}
                  onDelete={handleDelete}
                />
              ))
            )}
          </Dropzone>
        </Box>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Box mt={2} display="flex" justifyContent="space-around">
            <Box display="flex" alignItems="center" width="100%">
              <Box flexGrow={1} borderBottom="1px solid black" />
              <Typography variant="body1" mx={2}>
                OR
              </Typography>
              <Box flexGrow={1} borderBottom="1px solid black" />
            </Box>
          </Box>
          <div style={{ display: 'flex', marginLeft: '30%' }}>
            <AddButton disabled><FileUploadIcon />Create Manually</AddButton>
            <AddButton onClick={handleDownloadTemplateClick}><FileDownloadIcon /> Download Template</AddButton>
          </div>
        </div>
      </Box>

      <SnackbarNotification
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
      />
    </Container>
  );
};

export default UploadFileVendorAllocation;
